import React from 'react';
import { useMediaQuery } from '@mui/material';
import { useSelector } from 'react-redux';
import { combineClassNames } from 'app/functions';
import {
    extractHrefFromHtmlLink,
    extractLinkLabelFromHtml,
    normalizeHotYoterHtmlContent,
} from '../utils/hotYoterHtml';

const sanitizeHtmlForRtl = (html) => html
    .replace(/\sdir=["']ltr["']/gi, ' dir="rtl"');

const renderRichText = (content) => {
    const html = normalizeHotYoterHtmlContent(content);

    if (html && /<[a-z][\s\S]*>/i.test(html)) {
        return (
            <span
                dir="rtl"
                dangerouslySetInnerHTML={{ __html: sanitizeHtmlForRtl(html) }}
            />
        );
    }

    return content;
};

export const Content = ({
    children,
    className,
    testId,
    hasBackgroundImage = false,
}) => (
    <div
        className={combineClassNames(
            'hot-yoter-page-info__content',
            hasBackgroundImage && 'hot-yoter-page-info__content_has-bg',
            className,
        )}
        dir="rtl"
        testId={testId}
    >
        {children}
    </div>
);

export const HotYoterPageInfo = ({
    headerText,
    pageTitle,
    hasBackgroundImage = false,
    cardRowHeight = null,
    regulationsLink = null,
    regulationsLabel = null,
    onRegulationsPress,
    testId = 'hot-yoter-page-info',
}) => {
    const deviceState = useSelector((store) => store.state.deviceState);
    const isMobileViewport = useMediaQuery('(max-width:767px)');
    const isMobile = deviceState === 'mobile' || isMobileViewport;
    const title = headerText?.title ?? pageTitle;
    const subtitle = headerText?.subtitle ?? null;
    const linkUrl = extractHrefFromHtmlLink(headerText?.link)
        ?? headerText?.linkUrl
        ?? headerText?.regulationsUrl
        ?? null;
    const linkLabel = extractLinkLabelFromHtml(headerText?.link)
        ?? headerText?.linkLabel
        ?? 'לתקנון הטבות הוטיותר';
    const showDesktopRegulationsLink = Boolean(regulationsLink) && !isMobile;

    if (!title && !subtitle && !linkUrl && !showDesktopRegulationsLink) {
        return null;
    }

    const handleLinkPress = () => {
        if (!linkUrl) {
            return;
        }

        if (onRegulationsPress) {
            onRegulationsPress(linkUrl);
            return;
        }

        window.open(linkUrl, '_blank', 'noopener,noreferrer');
    };

    const syncCardRowHeight = !isMobile && cardRowHeight != null;

    return (
        <div
            className={combineClassNames(
                'hot-yoter-page-info',
                !isMobile && 'hot-yoter-page-info_desktop',
                hasBackgroundImage && 'hot-yoter-page-info_has-bg',
                syncCardRowHeight && 'hot-yoter-page-info_height-sync',
            )}
            style={syncCardRowHeight ? { height: cardRowHeight, minHeight: cardRowHeight } : undefined}
            testId={`${testId}-root`}
        >
            <Content
                testId={`${testId}-content`}
                hasBackgroundImage={hasBackgroundImage}
            >
                {title ? (
                    <div className="hot-yoter-page-info__title" testId={`${testId}-title`}>
                        {title}
                    </div>
                ) : null}
                {subtitle ? (
                    <div className="hot-yoter-page-info__subtitle" testId={`${testId}-subtitle`}>
                        {subtitle}
                    </div>
                ) : null}
                {showDesktopRegulationsLink ? (
                    <button
                        type="button"
                        className="hot-yoter-page-info__regulations-link"
                        onClick={() => onRegulationsPress?.(regulationsLink)}
                        testId={`${testId}-regulations-link`}
                    >
                        {regulationsLabel ?? linkLabel}
                    </button>
                ) : null}
            </Content>
            {linkUrl && isMobile ? (
                <button
                    type="button"
                    className="hot-yoter-page-info__link"
                    onClick={handleLinkPress}
                    testId={`${testId}-link`}
                >
                    {linkLabel}
                </button>
            ) : null}
        </div>
    );
};
